/**
 * Console Protection System
 * Detects and warns users about potentially malicious code pasted into DevTools
 */

let pasteWarningShown = false;
let suspiciousActivityCount = 0;

/**
 * Detect paste events in the console
 */
export const initConsoleProtection = () => {
    // Monitor for paste events
    let lastPasteTime = 0;

    document.addEventListener('paste', (e) => {
        const now = Date.now();

        // If pasting rapidly or in console context
        if (now - lastPasteTime < 1000 || !document.hasFocus()) {
            suspiciousActivityCount++;
            showPasteWarning();
        }

        lastPasteTime = now;
    });

    // Override console methods to detect suspicious patterns
    const originalLog = console.log;
    const originalError = console.error;

    console.log = function (...args) {
        detectSuspiciousCode(args);
        return originalLog.apply(console, args);
    };

    console.error = function (...args) {
        detectSuspiciousCode(args);
        return originalError.apply(console, args);
    };

    // Detect DevTools opening
    detectDevToolsOpen();
};

/**
 * Show prominent warning when paste is detected
 */
const showPasteWarning = () => {
    if (pasteWarningShown) return;

    console.clear();

    console.log(
        '%c⛔ STOP! SECURITY ALERT ⛔',
        'background: #dc2626; color: white; font-size: 40px; font-weight: bold; padding: 20px; text-align: center; border: 5px solid #991b1b;'
    );

    console.log('\n\n');

    console.log(
        '%c🚨 YOU ARE BEING SCAMMED! 🚨',
        'background: #ef4444; color: white; font-size: 24px; font-weight: bold; padding: 15px;'
    );

    console.log('\n');

    console.log(
        '%cIf someone told you to paste code here, they are trying to STEAL YOUR ACCOUNT!',
        'color: #dc2626; font-size: 18px; font-weight: bold;'
    );

    console.log('\n');

    console.log('%c❌ DO NOT PASTE ANY CODE HERE', 'color: #ef4444; font-size: 16px; font-weight: bold;');
    console.log('%c❌ DO NOT RUN ANY COMMANDS', 'color: #ef4444; font-size: 16px; font-weight: bold;');
    console.log('%c❌ CLOSE THIS WINDOW IMMEDIATELY', 'color: #ef4444; font-size: 16px; font-weight: bold;');

    console.log('\n');

    console.log(
        '%cWhat scammers are trying to do:',
        'color: #f59e0b; font-size: 14px; font-weight: bold;'
    );
    console.log('%c• Steal your login credentials', 'color: #f59e0b; font-size: 12px;');
    console.log('%c• Access your account', 'color: #f59e0b; font-size: 12px;');
    console.log('%c• Steal your money', 'color: #f59e0b; font-size: 12px;');
    console.log('%c• Use your account for fraud', 'color: #f59e0b; font-size: 12px;');

    console.log('\n');

    console.log(
        '%c✅ If you see this warning, you are SAFE - just close DevTools now',
        'background: #10b981; color: white; font-size: 14px; font-weight: bold; padding: 10px;'
    );

    console.log('\n');

    console.log(
        '%c📞 Report this to: security@kclogistics.mw',
        'color: #3b82f6; font-size: 14px; font-weight: bold;'
    );

    pasteWarningShown = true;

    // Log the incident
    logSecurityIncident('paste_detected');
};

/**
 * Detect suspicious code patterns
 */
const detectSuspiciousCode = (args: any[]) => {
    const argsString = JSON.stringify(args).toLowerCase();

    const suspiciousPatterns = [
        'localstorage',
        'sessionstorage',
        'document.cookie',
        'fetch(',
        'xmlhttprequest',
        'eval(',
        'function(',
        '=>',
        'token',
        'password',
        'auth',
        'session',
        '.send(',
        'navigator.sendbeacon'
    ];

    let matchCount = 0;
    for (const pattern of suspiciousPatterns) {
        if (argsString.includes(pattern)) {
            matchCount++;
        }
    }

    // If multiple suspicious patterns detected
    if (matchCount >= 3) {
        suspiciousActivityCount++;
        showPasteWarning();
    }
};

/**
 * Detect when DevTools is opened
 */
const detectDevToolsOpen = () => {
    const threshold = 160;
    let devtoolsOpen = false;

    const check = () => {
        const widthThreshold = window.outerWidth - window.innerWidth > threshold;
        const heightThreshold = window.outerHeight - window.innerHeight > threshold;

        if (widthThreshold || heightThreshold) {
            if (!devtoolsOpen) {
                devtoolsOpen = true;
                showDevToolsWarning();
            }
        } else {
            devtoolsOpen = false;
        }
    };

    setInterval(check, 1000);
};

/**
 * Show warning when DevTools is opened
 */
const showDevToolsWarning = () => {
    console.log('\n');
    console.log(
        '%c⚠️ DEVELOPER TOOLS DETECTED',
        'background: #f59e0b; color: white; font-size: 16px; font-weight: bold; padding: 10px;'
    );
    console.log('\n');
    console.log(
        '%c🛡️ Security Reminder:',
        'color: #3b82f6; font-size: 14px; font-weight: bold;'
    );
    console.log('%c• Never paste code from untrusted sources', 'color: #6b7280; font-size: 12px;');
    console.log('%c• No legitimate service will ask you to paste code here', 'color: #6b7280; font-size: 12px;');
    console.log('%c• Scammers use "free premium" or "fix errors" as bait', 'color: #6b7280; font-size: 12px;');
    console.log('\n');
};

/**
 * Log security incidents for monitoring
 */
const logSecurityIncident = (type: string) => {
    // In production, this would send to your security monitoring system
    if (import.meta.env?.PROD) {
        // TODO: Implement backend security logging endpoint
        // fetch('/api/security/incident', {
        //     method: 'POST',
        //     headers: { 'Content-Type': 'application/json' },
        //     body: JSON.stringify({
        //         type,
        //         timestamp: new Date().toISOString(),
        //         userAgent: navigator.userAgent,
        //         url: window.location.href
        //     })
        // }).catch(() => {
        //     // Silently fail - don't expose to user
        // });
    }
};

/**
 * Disable certain dangerous console methods in production
 */
export const lockdownConsole = () => {
    if (import.meta.env?.PROD) {
        // Override eval to warn users
        const originalEval = window.eval;
        window.eval = function (code: string) {
            console.error('⛔ eval() is disabled for security reasons');
            showPasteWarning();
            logSecurityIncident('eval_attempt');
            return null;
        } as any;

        // Warn about Function constructor
        const OriginalFunction = Function;
        (window as any).Function = function (...args: any[]) {
            console.error('⛔ Function constructor is disabled for security reasons');
            showPasteWarning();
            logSecurityIncident('function_constructor_attempt');
            return function () { };
        };
    }
};

/**
 * Get security status
 */
export const getSecurityStatus = () => {
    return {
        pasteWarningShown,
        suspiciousActivityCount,
        protectionActive: true
    };
};
